import { useState, useRef } from 'react'
import { TurnstileWidget } from './TurnstileWidget'

interface UserSetupFormProps {
  initialUsername?: string
  initialEmail?: string
  onSubmit: (_username: string, _email: string) => void
  onTurnstileVerify?: (_token: string) => void
  validateMX?: (_email: string) => Promise<{ valid: boolean; error?: string }>
}

interface ValidationErrors {
  username?: string | null
  email?: string | null
}

// Validation functions
const validateEmail = (email: string): string | null => {
  if (!email.trim()) {
    return 'Email is required'
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!emailRegex.test(email.trim())) {
    return 'Please enter a valid email address'
  }

  return null
}

const validateUsername = (username: string): string | null => {
  if (!username.trim()) {
    return 'Username is required'
  }

  const trimmedUsername = username.trim()

  if (trimmedUsername.length < 2) {
    return 'Username must be at least 2 characters long'
  }

  if (trimmedUsername.length > 30) {
    return 'Username must be less than 30 characters'
  }

  // Allow letters, numbers, spaces, hyphens, underscores, and dots
  const usernameRegex = /^[a-zA-Z0-9\s._-]+$/
  if (!usernameRegex.test(trimmedUsername)) {
    return 'Username can only contain letters, numbers, spaces, dots, hyphens, and underscores'
  }

  return null
}

export function UserSetupForm({
  initialUsername = '',
  initialEmail = '',
  onSubmit,
  onTurnstileVerify,
  validateMX,
}: UserSetupFormProps) {
  const [username, setUsername] = useState(initialUsername)
  const [email, setEmail] = useState(initialEmail)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [touched, setTouched] = useState<{ username: boolean; email: boolean }>({
    username: false,
    email: false,
  })
  const [isValidatingMX, setIsValidatingMX] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileWidgetRef = useRef<{ reset: () => void } | null>(null)

  const validateForm = (): boolean => {
    const usernameError = validateUsername(username)
    const emailError = validateEmail(email)

    const newErrors: ValidationErrors = {}
    if (usernameError) newErrors.username = usernameError
    if (emailError) newErrors.email = emailError

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleUsernameChange = (value: string) => {
    setUsername(value)
    if (touched.username) {
      const error = validateUsername(value)
      setErrors((prev) => ({ ...prev, username: error }))
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (touched.email) {
      const error = validateEmail(value)
      setErrors((prev) => ({ ...prev, email: error }))
    }
  }

  const handleUsernameBlur = () => {
    setTouched((prev) => ({ ...prev, username: true }))
    const error = validateUsername(username)
    setErrors((prev) => ({ ...prev, username: error }))
  }

  const handleEmailBlur = async () => {
    setTouched((prev) => ({ ...prev, email: true }))
    const error = validateEmail(email)
    setErrors((prev) => ({ ...prev, email: error }))

    // Validate MX records if email format is valid and validateMX is provided
    if (!error && validateMX && email.trim()) {
      setIsValidatingMX(true)
      try {
        const mxResult = await validateMX(email.trim())
        if (!mxResult.valid) {
          setErrors((prev) => ({ ...prev, email: mxResult.error || 'Invalid email domain' }))
        }
      } catch (err) {
        console.error('MX validation error:', err)
        // Don't block form submission on MX validation failure
      } finally {
        setIsValidatingMX(false)
      }
    }
  }

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token)
    onTurnstileVerify?.(token)
  }

  const handleTurnstileError = () => {
    setTurnstileToken(null)
    turnstileWidgetRef.current?.reset()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouched({ username: true, email: true })

    if (!validateForm()) {
      return
    }

    // Validate MX records if not already validated
    if (validateMX && email.trim() && !errors.email) {
      setIsValidatingMX(true)
      try {
        const mxResult = await validateMX(email.trim())
        if (!mxResult.valid) {
          setErrors((prev) => ({ ...prev, email: mxResult.error || 'Invalid email domain' }))
          setIsValidatingMX(false)
          return
        }
      } catch (err) {
        console.error('MX validation error:', err)
        // Continue with submission even if MX validation fails
      } finally {
        setIsValidatingMX(false)
      }
    }

    // Submit form
    onSubmit(username.trim(), email.trim())
  }

  const isFormValid = !errors.username && !errors.email && username.trim() && email.trim()

  return (
    <div className="bg-chat-primary flex h-full items-center justify-between rounded-b-[12px] p-6">
      <div className="h-full w-full text-start">
        <h2 className="mb-2 text-lg font-bold text-white">Let&apos;s dive in!</h2>
        <p className="t mb-4 w-[266px] text-[40px] leading-[32px] text-white">
          Share your details to kick things off.
        </p>

        <form onSubmit={handleSubmit} className="mt-[48px] space-y-4">
          <div className="mb-0">
            <label htmlFor="username" className="mb-2 block text-left text-[12px] font-bold">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              onBlur={handleUsernameBlur}
              className={`!bg-chat-primary block w-full rounded-lg border px-3 py-2 text-sm !text-white placeholder-[#ffffff7a] shadow-sm transition-colors focus:outline-none ${errors.username && touched.username
                  ? 'border-red-400 focus:border-red-400'
                  : 'border-gray-300 focus:border-blue-400'
                }`}
              placeholder="Enter your username"
              autoFocus
              aria-invalid={errors.username && touched.username ? 'true' : 'false'}
              aria-describedby={errors.username && touched.username ? 'username-error' : undefined}
            />
            {/* Reserved space for error message */}
            <div className="mt-1 min-h-[18px]">
              {errors.username && touched.username && (
                <p id="username-error" className="text-[12px] text-red-400" role="alert">
                  {errors.username}
                </p>
              )}
            </div>
          </div>
          <div className="mb-0">
            <label htmlFor="email" className="mb-2 block text-left text-[12px] font-bold">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              className={`!bg-chat-primary block w-full rounded-lg border px-3 py-2 text-sm !text-white placeholder-[#ffffff7a] shadow-sm transition-colors focus:outline-none ${errors.email && touched.email
                  ? 'border-red-400 focus:border-red-400'
                  : 'border-gray-300 focus:border-blue-400'
                }`}
              placeholder="Enter your email"
              aria-invalid={errors.email && touched.email ? 'true' : 'false'}
              aria-describedby={errors.email && touched.email ? 'email-error' : undefined}
            />
            <div className="mt-1 min-h-[18px]">
              {errors.email && touched.email && (
                <p id="email-error" className="text-[12px] text-red-400" role="alert">
                  {errors.email}
                </p>
              )}
              {isValidatingMX && !errors.email && (
                <p className="text-[12px] text-gray-400" role="status">
                  Validating email domain...
                </p>
              )}
            </div>
          </div>

          {/* Turnstile Widget (invisible, rendered but hidden) */}
          <div className="hidden">
            <TurnstileWidget
              onVerify={handleTurnstileVerify}
              onError={handleTurnstileError}
            />
          </div>

          <button
            type="submit"
            disabled={!isFormValid}
            className="relative ml-auto flex max-h-[40px] cursor-pointer items-center justify-end focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="text-md flex h-[40px] items-center rounded-full bg-white px-6 py-3 font-bold text-black shadow-sm">
              send
            </div>
            <img src="/images/icons/redArrowRight.svg" alt="send arrow" width={40} height={40} />
          </button>
        </form>
      </div>
    </div>
  )
}
